import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { Mail } from 'lucide-react';
import {
  getInvites,
  createInvite,
  resendInvite,
  updateInvite,
  deleteInvite,
  updateStaffGrant,
  deleteStaffGrant,
  type AdminInvite,
  type ApiResult,
} from '../lib/api';
import { useTracks } from '../lib/data';

// Khalifa "invite candidates" view. All server strings rendered as React text.
// After every successful mutation the full list is re-fetched (no optimistic update).
// A 403 on any call locks the view sticky (forbiddenRef) — mirrors AdminPeople.

// Welcome is always auto-granted server-side (see functions/src/invites.ts
// WELCOME_TRACK_ID) — quick-select-by-tag excludes it from both the count and
// the union, since selecting it here would be redundant.
const WELCOME_TRACK_ID = 'track_welcome';

// UX hint only: the server decides staff vs candidate via ALLOWED_DOMAINS.
const STAFF_DOMAINS = ['cloudcompliance.app', 'henoyo.ai'];

/** Defensive: treat a missing/non-array `tags` as []. */
function trackTags(t: { tags?: string[] }): string[] {
  return Array.isArray(t.tags) ? t.tags : [];
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error'; message: string }
  | { phase: 'forbidden' }
  | { phase: 'ready'; invites: AdminInvite[] };

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function isExpired(expiresAt: string | null): boolean {
  if (!expiresAt) return false;
  return new Date(expiresAt).getTime() < Date.now();
}

export default function AdminInvites() {
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [tick, setTick] = useState(0);
  const forbiddenRef = useRef(false);

  // Create-form state
  const [kind, setKind] = useState<'email' | 'domain'>('email');
  const [value, setValue] = useState('');
  const [selectedTrackIds, setSelectedTrackIds] = useState<string[]>([]);
  const [label, setLabel] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [formError, setFormError] = useState<string | null>(null);
  const [formEmailNote, setFormEmailNote] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Per-row busy + error state (keyed by invite id)
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  // Per-row resend state (keyed by invite id)
  const [resendingIds, setResendingIds] = useState<Set<string>>(new Set());
  const [resendMessages, setResendMessages] = useState<Record<string, { ok: boolean; msg: string }>>({});

  const { tracks } = useTracks();

  // Hint only: does the typed email/domain look like a teammate?
  const typedDomain =
    kind === 'email'
      ? (value.trim().toLowerCase().split('@')[1] ?? '')
      : value.trim().toLowerCase().replace(/^@/, '');
  const isStaffTarget = STAFF_DOMAINS.includes(typedDomain);

  // Distinct audience tags across all loaded tracks, and the non-Welcome
  // tracks carrying a given tag (Welcome is auto-granted server-side).
  const allTrackTags = Array.from(new Set(tracks.flatMap((t) => trackTags(t))));
  const nonWelcomeTracksForTag = (tag: string) =>
    tracks.filter(
      (t) =>
        t.id !== WELCOME_TRACK_ID &&
        (isStaffTarget || t.internal !== true) &&
        trackTags(t).includes(tag),
    );

  const selectAllByTag = (tag: string) => {
    const ids = nonWelcomeTracksForTag(tag).map((t) => t.id);
    setSelectedTrackIds((prev) => Array.from(new Set([...prev, ...ids])));
  };

  const reload = useCallback(() => {
    if (!forbiddenRef.current) setTick((t) => t + 1);
  }, []);

  useEffect(() => {
    if (forbiddenRef.current) return;
    let cancelled = false;
    setState({ phase: 'loading' });
    void (async () => {
      const res = await getInvites();
      if (cancelled || forbiddenRef.current) return;
      if (res.status === 403) {
        forbiddenRef.current = true;
        setState({ phase: 'forbidden' });
        return;
      }
      if (!res.ok) {
        setState({
          phase: 'error',
          message:
            res.status === 0
              ? 'Network error — check your connection.'
              : `Failed (HTTP ${res.status}).`,
        });
        return;
      }
      setState({
        phase: 'ready',
        invites: Array.isArray(res.data?.invites) ? res.data.invites : [],
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [tick]);

  /** Run a per-row mutation: set busy, clear row error, call action, reload on ok. */
  const mutateRow = useCallback(async <T,>(
    id: string,
    action: () => Promise<ApiResult<T>>,
  ): Promise<ApiResult<T>> => {
    setBusyIds((prev) => new Set([...prev, id]));
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
    const res = await action();
    if (res.status === 403) {
      forbiddenRef.current = true;
      setState({ phase: 'forbidden' });
    }
    setBusyIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    if (res.ok) setTick((t) => t + 1);
    return res;
  }, []);

  const setRowError = (id: string, msg: string) =>
    setRowErrors((prev) => ({ ...prev, [id]: msg }));

  const dismissRowError = (id: string) =>
    setRowErrors((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });

  const emailSentNote = (status: string | undefined, inviteKind: 'email' | 'domain'): string | null => {
    if (inviteKind === 'domain' || status === 'skipped' || status === undefined) return null;
    if (status === 'sent') return 'Invite email sent ✓';
    if (status === 'no_smtp') return 'Invite saved — email is not configured (SMTP)';
    if (status === 'blocked') return 'Invite saved — email not sent (staff address)';
    if (status === 'error') return 'Invite saved — email failed to send, use Resend';
    return null;
  };

  const handleCreate = async () => {
    if (!value.trim()) return;
    if (isStaffTarget && selectedTrackIds.length === 0) return;
    setSubmitting(true);
    setFormError(null);
    setFormEmailNote(null);
    const body = {
      kind,
      value: value.trim(),
      // Sent as picked: the server decides staff vs candidate and rejects internal
      // kushtis on a candidate invite.
      accessTrackIds: selectedTrackIds,
      ...(label.trim() ? { label: label.trim() } : {}),
      ...(!isStaffTarget && expiryDate ? { expiresAt: `${expiryDate}T23:59:59.999Z` } : {}),
      sendEmail: isStaffTarget ? false : sendEmail,
    };
    const res = await createInvite(body);
    if (res.status === 403) {
      forbiddenRef.current = true;
      setState({ phase: 'forbidden' });
      setSubmitting(false);
      return;
    }
    if (!res.ok) {
      setFormError(res.data.detail ?? res.data.error ?? `Error ${res.status}`);
      setSubmitting(false);
      return;
    }
    // Surface email result
    const note =
      res.data.audience === 'staff'
        ? `Assigned to teammate. ${res.data.assignedNow ?? 0} course assignment(s) made now; the rest apply at their next sign-in.`
        : emailSentNote(res.data.emailSent, kind);
    // Reset form on success
    setValue('');
    setLabel('');
    setExpiryDate('');
    setSelectedTrackIds([]);
    setFormError(null);
    setFormEmailNote(note);
    setSubmitting(false);
    setTick((t) => t + 1);
  };

  const resendErrorText = (code: string | undefined): string => {
    if (code === 'smtp_unconfigured') return 'Email isn\'t configured (SMTP secret).';
    if (code === 'staff_email') return 'That\'s a staff address.';
    if (code === 'email_send_failed') return 'Send failed — try again.';
    if (code === 'invite_not_active') return 'Invite is revoked.';
    if (code === 'not_an_email_invite') return 'Not an email invite.';
    if (code === 'invite_not_found') return 'Invite not found.';
    return `Send failed (${code ?? 'unknown'}).`;
  };

  const handleResend = async (id: string) => {
    setResendingIds((prev) => new Set([...prev, id]));
    setResendMessages((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
    const res = await resendInvite(id);
    setResendingIds((prev) => {
      const n = new Set(prev);
      n.delete(id);
      return n;
    });
    if (res.status === 403) {
      forbiddenRef.current = true;
      setState({ phase: 'forbidden' });
      return;
    }
    if (res.ok) {
      setResendMessages((prev) => ({ ...prev, [id]: { ok: true, msg: 'Email sent ✓' } }));
    } else {
      setResendMessages((prev) => ({
        ...prev,
        [id]: { ok: false, msg: resendErrorText((res.data as { error?: string }).error) },
      }));
    }
  };

  const isStaffRow = (id: string) =>
    state.phase === 'ready' && state.invites.find((i) => i.id === id)?.audience === 'staff';

  const handleRevoke = async (id: string) => {
    const staff = isStaffRow(id);
    if (staff && !window.confirm('Revoke this teammate grant? This stops future course assignment; courses already assigned stay.')) return;
    const res = await mutateRow(id, () =>
      staff ? updateStaffGrant(id, { status: 'revoked' }) : updateInvite(id, { status: 'revoked' }),
    );
    if (!res.ok && res.status !== 403) {
      setRowError(id, `Revoke failed (${res.data.error ?? res.status}).`);
    }
  };

  const handleReactivate = async (id: string) => {
    const staff = isStaffRow(id);
    const res = await mutateRow(id, () =>
      staff ? updateStaffGrant(id, { status: 'active' }) : updateInvite(id, { status: 'active' }),
    );
    if (!res.ok && res.status !== 403) {
      setRowError(id, `Reactivate failed (${res.data.error ?? res.status}).`);
    }
  };

  const handleDelete = async (id: string, inviteValue: string) => {
    const confirmText = isStaffRow(id)
      ? `Delete teammate grant for "${inviteValue}"? This stops future course assignment; courses already assigned stay.`
      : `Delete invite for "${inviteValue}"? This cannot be undone.`;
    if (!window.confirm(confirmText)) return;
    const staff = isStaffRow(id);
    const res = await mutateRow(id, () => (staff ? deleteStaffGrant(id) : deleteInvite(id)));
    if (!res.ok && res.status !== 403) {
      setRowError(id, `Delete failed (${res.data.error ?? res.status}).`);
    }
  };

  return (
    <div>
      {/* Sticky forbidden */}
      {state.phase === 'forbidden' && (
        <div
          className="mt-6 rounded-xl border border-warn-500/40 bg-paper-0 p-4 text-sm dark:bg-clay-surface"
          role="alert"
        >
          <p className="font-semibold text-ink-900 dark:text-paper-50">Not authorized</p>
          <p className="mt-1 text-ink-500 dark:text-paper-300">
            This view is for Khalifas (admins) only.
          </p>
        </div>
      )}

      {state.phase === 'loading' && (
        <p className="mt-6 text-sm text-ink-400">Loading…</p>
      )}

      {state.phase === 'error' && (
        <div className="mt-6 text-sm text-terracotta-700">
          {state.message}{' '}
          <button onClick={reload} className="underline">
            Retry
          </button>
        </div>
      )}

      {state.phase === 'ready' && (
        <>
          {/* ── Create invite form ── */}
          <div className="mt-5 rounded-xl border border-rule bg-paper-0 p-4 dark:border-clay-rule dark:bg-clay-surface">
            <h2 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
              Create invite
            </h2>

            {/* Kind toggle */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs text-ink-500 dark:text-paper-300">Type:</span>
              <div className="inline-flex rounded-lg border border-rule bg-paper-50 p-0.5 dark:border-clay-rule dark:bg-clay-floor">
                {(['email', 'domain'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setKind(k)}
                    className={`rounded-md px-3 py-1 text-xs font-medium transition ${
                      kind === k
                        ? 'bg-terracotta-500 text-white'
                        : 'text-ink-500 hover:text-ink-900 dark:text-paper-300'
                    }`}
                  >
                    {k === 'email' ? 'Email' : 'Domain'}
                  </button>
                ))}
              </div>
            </div>

            {kind === 'domain' && (
              <p className="mt-1.5 text-xs text-ink-400">
                Free providers (gmail, outlook, …) must be invited as individual emails.
              </p>
            )}

            {/* Value input */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
                {kind === 'email' ? 'Email address' : 'Domain (e.g. company.com)'}
              </label>
              <input
                type={kind === 'email' ? 'email' : 'text'}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={kind === 'email' ? 'name@example.com' : 'company.com'}
                className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
              />
            </div>

            {isStaffTarget && (
              <p className="mt-1.5 text-xs text-ink-500 dark:text-paper-300">
                Teammate: they sign in with Google. These courses are assigned to them, including
                internal ones. No invite email is sent.
              </p>
            )}

            {/* Grant trails checkboxes */}
            <div className="mt-3">
              <p className="text-xs font-medium text-ink-500 dark:text-paper-300">Grant kushtis</p>
              <p className="mt-0.5 text-xs text-ink-400">
                {isStaffTarget
                  ? 'Pick at least one kushti for a teammate.'
                  : 'The Welcome kushti is always included.'}
              </p>
              {tracks.length === 0 ? (
                <p className="mt-1.5 text-xs text-ink-400">No published kushtis yet.</p>
              ) : (
                <>
                  {/* Quick-select by audience tag (excludes Welcome — always auto-included) */}
                  {allTrackTags.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      {allTrackTags.filter((tag) => nonWelcomeTracksForTag(tag).length > 0).map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          onClick={() => selectAllByTag(tag)}
                          className="rounded-full border border-rule px-2.5 py-0.5 text-xs font-medium text-ink-500 transition hover:bg-paper-100 focus:outline-none focus:ring-2 focus:ring-terracotta-500 dark:border-clay-rule dark:text-paper-300 dark:hover:bg-clay-raised"
                        >
                          Select all #{tag} ({nonWelcomeTracksForTag(tag).length})
                        </button>
                      ))}
                      <button
                        type="button"
                        onClick={() => setSelectedTrackIds([])}
                        className="rounded-full border border-rule px-2.5 py-0.5 text-xs font-medium text-ink-500 transition hover:bg-paper-100 focus:outline-none focus:ring-2 focus:ring-terracotta-500 dark:border-clay-rule dark:text-paper-300 dark:hover:bg-clay-raised"
                      >
                        Clear
                      </button>
                    </div>
                  )}
                  <div className="mt-2 flex flex-col gap-1.5">
                    {tracks.map((t) => (
                      <label
                        key={t.id}
                        className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 dark:text-paper-200"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTrackIds.includes(t.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedTrackIds((prev) => [...prev, t.id]);
                            } else {
                              setSelectedTrackIds((prev) => prev.filter((id) => id !== t.id));
                            }
                          }}
                          className="h-3.5 w-3.5 accent-terracotta-500"
                        />
                        <span>{t.title}</span>
                        {t.internal === true && !isStaffTarget && (
                          <span className="text-[10px] text-ink-400">Teammates only</span>
                        )}
                        {trackTags(t).length > 0 && (
                          <span className="flex flex-wrap gap-1">
                            {trackTags(t).map((tag) => (
                              <span
                                key={tag}
                                title={`#${tag}`}
                                className="rounded-full bg-paper-100 px-1.5 py-0.5 text-[10px] font-medium text-ink-400 dark:bg-clay-raised dark:text-paper-300"
                              >
                                #{tag}
                              </span>
                            ))}
                          </span>
                        )}
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Optional label */}
            <div className="mt-3">
              <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
                Label{' '}
                <span className="font-normal text-ink-400">(optional)</span>
              </label>
              <input
                type="text"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Cohort Spring 2026"
                className="mt-1 w-full rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50 dark:placeholder:text-ink-500"
              />
            </div>

            {/* Optional expiry date (candidates only) */}
            {!isStaffTarget && (
            <div className="mt-3">
              <label className="block text-xs font-medium text-ink-500 dark:text-paper-300">
                Expires on{' '}
                <span className="font-normal text-ink-400">(optional)</span>
              </label>
              <input
                type="date"
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
                className="mt-1 rounded-lg border border-rule bg-paper-0 px-3 py-1.5 text-sm text-ink-900 focus:outline-none focus:ring-1 focus:ring-terracotta-500 dark:border-clay-rule dark:bg-clay-floor dark:text-paper-50"
              />
            </div>
            )}

            {/* Send email checkbox (candidates only) */}
            {!isStaffTarget && (
            <div className="mt-3">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-ink-700 dark:text-paper-200">
                <input
                  type="checkbox"
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                  className="h-3.5 w-3.5 accent-terracotta-500"
                />
                Send invite email now
                <span className="text-xs text-ink-400">(email invites only)</span>
              </label>
            </div>
            )}

            {/* Form error */}
            {formError && (
              <p className="mt-2 text-xs text-terracotta-700 dark:text-terracotta-400">
                {formError}
              </p>
            )}

            {/* Email sent note */}
            {formEmailNote && (
              <p className="mt-2 text-xs text-verdant-700 dark:text-verdant-500">
                {formEmailNote}
              </p>
            )}

            {/* Submit button */}
            <button
              type="button"
              onClick={() => void handleCreate()}
              disabled={submitting || !value.trim() || (isStaffTarget && selectedTrackIds.length === 0)}
              className="mt-4 rounded-lg bg-terracotta-500 px-4 py-1.5 text-sm font-medium text-white transition hover:bg-terracotta-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {submitting ? 'Creating…' : 'Create invite'}
            </button>
          </div>

          {/* ── List header ── */}
          <div className="mt-6 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink-700 dark:text-paper-200">
              {state.invites.length}{' '}
              {state.invites.length === 1 ? 'invite' : 'invites'}
            </h2>
            <button
              onClick={reload}
              className="rounded-lg border border-rule px-2.5 py-1 text-xs font-medium text-ink-500 hover:bg-paper-100 dark:border-clay-rule dark:hover:bg-clay-raised"
            >
              Refresh
            </button>
          </div>

          {/* ── Invites table ── */}
          <div className="mt-2 overflow-x-auto rounded-xl border border-rule dark:border-clay-rule">
            <table className="w-full text-sm">
              <thead className="bg-paper-100 text-left text-xs uppercase tracking-wide text-ink-400 dark:bg-clay-raised/40">
                <tr>
                  <th className="px-3 py-2 font-medium">Type</th>
                  <th className="px-3 py-2 font-medium">Value</th>
                  <th className="px-3 py-2 font-medium">Kushtis</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium">Expires</th>
                  <th className="px-3 py-2 font-medium">Created</th>
                  <th className="px-3 py-2 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {state.invites.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-3 py-6 text-center text-ink-400"
                    >
                      No invites yet.
                    </td>
                  </tr>
                ) : (
                  state.invites.map((inv) => {
                    const busy = busyIds.has(inv.id);
                    const rowErr = rowErrors[inv.id];
                    const expired = inv.audience !== 'staff' && isExpired(inv.expiresAt);
                    return (
                      <Fragment key={inv.id}>
                        <tr className="border-t border-rule dark:border-clay-rule">
                          {/* Type pill */}
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                inv.kind === 'email'
                                  ? 'bg-state-200 text-state-700 dark:bg-state-700/30 dark:text-state-200'
                                  : 'bg-paper-100 text-ink-600 dark:bg-clay-raised dark:text-paper-300'
                              }`}
                            >
                              {inv.kind}
                            </span>
                            {inv.audience === 'staff' && (
                              <span className="ml-1.5 inline-flex rounded-full bg-terracotta-500/15 px-2 py-0.5 text-xs font-semibold text-terracotta-700 dark:text-terracotta-400">
                                Teammate
                              </span>
                            )}
                          </td>

                          {/* Value — mono, React text */}
                          <td className="px-3 py-2 font-mono text-xs text-ink-700 dark:text-paper-200">
                            {inv.value}
                          </td>

                          {/* Granted trails count */}
                          <td className="px-3 py-2 text-xs text-ink-500 dark:text-paper-300">
                            {inv.accessTrackIds.length === 0
                              ? '—'
                              : `${inv.accessTrackIds.length} kushti${inv.accessTrackIds.length === 1 ? '' : 's'}`}
                          </td>

                          {/* Status badge — active=verdant, revoked=muted, expired hint */}
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                                inv.status === 'active' && !expired
                                  ? 'bg-verdant-200/60 text-verdant-700 dark:bg-verdant-700/20 dark:text-verdant-500'
                                  : 'bg-paper-100 text-ink-400 dark:bg-clay-raised dark:text-paper-300'
                              }`}
                            >
                              {inv.status === 'active' && expired ? 'expired' : inv.status}
                            </span>
                          </td>

                          {/* Expires */}
                          <td className="px-3 py-2 text-xs text-ink-500 dark:text-paper-300">
                            {inv.audience === 'staff' ? '—' : fmtDate(inv.expiresAt)}
                          </td>

                          {/* Created */}
                          <td className="px-3 py-2 text-xs text-ink-500 dark:text-paper-300">
                            {fmtDate(inv.createdAt)}
                          </td>

                          {/* Actions */}
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap items-center gap-3">
                              {inv.status === 'active' ? (
                                <button
                                  onClick={() => void handleRevoke(inv.id)}
                                  disabled={busy}
                                  className="text-xs text-ink-500 hover:text-terracotta-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  Revoke
                                </button>
                              ) : (
                                <button
                                  onClick={() => void handleReactivate(inv.id)}
                                  disabled={busy}
                                  className="text-xs text-verdant-700 hover:underline disabled:cursor-not-allowed disabled:opacity-50 dark:text-verdant-500"
                                >
                                  Reactivate
                                </button>
                              )}
                              {/* Resend email — active email invites only */}
                              {inv.audience !== 'staff' && inv.kind === 'email' && inv.status === 'active' && (
                                <button
                                  onClick={() => void handleResend(inv.id)}
                                  disabled={busy || resendingIds.has(inv.id)}
                                  title="Resend magic-link email"
                                  className="flex items-center gap-1 text-xs text-ink-500 hover:text-terracotta-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                  <Mail size={12} />
                                  {resendingIds.has(inv.id) ? 'Sending…' : 'Resend'}
                                </button>
                              )}
                              <button
                                onClick={() => void handleDelete(inv.id, inv.value)}
                                disabled={busy}
                                className="text-xs text-terracotta-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>

                        {/* Per-row resend message */}
                        {resendMessages[inv.id] && (
                          <tr className="border-t border-rule dark:border-clay-rule">
                            <td
                              colSpan={7}
                              className={`px-3 pb-2 pt-1 text-xs ${
                                resendMessages[inv.id].ok
                                  ? 'text-verdant-700 dark:text-verdant-500'
                                  : 'text-terracotta-700 dark:text-terracotta-400'
                              }`}
                            >
                              {resendMessages[inv.id].msg}{' '}
                              <button
                                onClick={() =>
                                  setResendMessages((prev) => {
                                    const n = { ...prev };
                                    delete n[inv.id];
                                    return n;
                                  })
                                }
                                className="underline"
                              >
                                Dismiss
                              </button>
                            </td>
                          </tr>
                        )}

                        {/* Per-row error */}
                        {rowErr && (
                          <tr className="border-t border-rule dark:border-clay-rule">
                            <td
                              colSpan={7}
                              className="px-3 pb-2 pt-1 text-xs text-terracotta-700 dark:text-terracotta-400"
                            >
                              {rowErr}{' '}
                              <button
                                onClick={() => dismissRowError(inv.id)}
                                className="underline"
                              >
                                Dismiss
                              </button>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
